<script setup lang="ts">
  import { computed, type HTMLAttributes, type Ref } from 'vue'
  import {
    CalendarRoot,
    type CalendarRootEmits,
    type CalendarRootProps,
    useDateFormatter,
    useForwardPropsEmits
  } from 'radix-vue'
  import { createYear, createYearRange, toDate } from 'radix-vue/date'
  import { type DateValue, getLocalTimeZone, today } from '@internationalized/date'
  import { useVModel } from '@vueuse/core'
  import {
    CalendarCell,
    CalendarCellTrigger,
    CalendarGrid,
    CalendarGridBody,
    CalendarGridHead,
    CalendarGridRow,
    CalendarHeadCell,
    CalendarHeader,
    CalendarHeading
  } from '@/components/ui/calendar'
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
  } from '@/components/ui/select'
  import { cn } from '@/lib/utils'
  import { debounce } from '@/lib/timing'

  const props = withDefaults(
    defineProps<
      CalendarRootProps & { class?: HTMLAttributes['class'] } & {
        inputField: false
      }
    >(),
    {
      modelValue: undefined,
      placeholder() {
        return today(getLocalTimeZone())
      },
      weekdayFormat: 'short'
    }
  )
  const emits = defineEmits<CalendarRootEmits>()

  const delegatedProps = computed(() => {
    const { class: _, placeholder: __, ...delegated } = props

    return delegated
  })

  const placeholder = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: today(getLocalTimeZone())
  }) as Ref<DateValue>

  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  const formatter = useDateFormatter('de-DE')

  const yearRange = computed(() => {
    // Changing the startIndex and endIndex is not working as expected
    return createYearRange({
      start: props.placeholder.subtract({ years: 100 }),
      end: props.placeholder.add({ years: 10 })
    })
  })

  const getYear = (date: DateValue) => {
    let year = date.getFullYear()
    if (year < 1900 || year > 2100 || year.toString().length !== 4) {
      console.error('Year out of range')
      year = placeholder.value.year
    }
    return year
  }

  const handleInput = (event: Event) => {
    const date = new Date((event.target as HTMLInputElement).value)
    if (!placeholder.value) {
      placeholder.value = props.placeholder
    }
    if (date.toString() === 'Invalid Date') return
    placeholder.value = placeholder.value.set({
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: getYear(date)
    })
  }

  const debouncedHandleInput = debounce(handleInput, 800)
</script>

<template>
  <CalendarRoot
    v-slot="{ date, grid, weekDays }"
    v-model:placeholder="placeholder"
    v-bind="forwarded"
    :class="cn('rounded-md border p-3', props.class)"
  >
    <CalendarHeader>
      <CalendarHeading class="flex w-full flex-col items-center justify-between gap-2">
        <input
          v-if="props.inputField"
          data-cy="questionnaireDateInput"
          class="date-input focus:border-1 w-full shrink grow basis-0 self-stretch rounded-lg border border-gray-200
            bg-white font-normal shadow focus:border-violet-500 focus:outline-none sm:px-5"
          placeholder="Ihre Antwort...."
          type="date"
          :value="placeholder ?? props.placeholder"
          @input="debouncedHandleInput"
        />
        <div class="flex w-full items-center justify-between gap-2">
          <Select
            :model-value="
              placeholder ? placeholder.month.toString() : props.placeholder.month.toString()
            "
            @update:model-value="
              (v) => {
                if (!v || !placeholder) return
                if (Number(v) === placeholder?.month) return
                placeholder = placeholder.set({
                  month: Number(v)
                })
              }
            "
          >
            <SelectTrigger
              aria-label="Monat wählen"
              class="w-[60%] border border-gray-200 bg-white shadow focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <SelectValue placeholder="Monat wählen" />
            </SelectTrigger>
            <SelectContent class="max-h-[200px]">
              <SelectItem
                v-for="month in createYear({ dateObj: date })"
                :key="month.toString()"
                :value="month.month.toString()"
              >
                {{ formatter.custom(toDate(month), { month: 'long' }) }}
              </SelectItem>
            </SelectContent>
          </Select>

          <Select
            :model-value="
              placeholder ? placeholder.year.toString() : props.placeholder.year.toString()
            "
            @update:model-value="
              (v) => {
                if (!v || !placeholder) return
                if (Number(v) === placeholder?.year) return
                placeholder = placeholder.set({
                  year: Number(v)
                })
              }
            "
          >
            <SelectTrigger
              aria-label="Jahr wählen"
              class="w-[40%] border border-gray-200 bg-white shadow focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <SelectValue placeholder="Jahr wählen" />
            </SelectTrigger>
            <SelectContent class="max-h-[200px]">
              <SelectItem
                v-for="yearValue in yearRange"
                :key="yearValue.toString()"
                :value="yearValue.year.toString()"
              >
                {{ yearValue.year }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CalendarHeading>
    </CalendarHeader>

    <div class="flex flex-col space-y-4 pt-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
      <CalendarGrid
        v-for="month in grid"
        :key="month.value.toString()"
      >
        <CalendarGridHead>
          <CalendarGridRow>
            <CalendarHeadCell
              v-for="day in weekDays"
              :key="day"
            >
              {{ day }}
            </CalendarHeadCell>
          </CalendarGridRow>
        </CalendarGridHead>
        <CalendarGridBody class="grid">
          <CalendarGridRow
            v-for="(weekDates, index) in month.rows"
            :key="`weekDate-${index}`"
            class="mt-2 w-full"
          >
            <CalendarCell
              v-for="weekDate in weekDates"
              :key="weekDate.toString()"
              :date="weekDate"
            >
              <CalendarCellTrigger
                :day="weekDate"
                :month="month.value"
              />
            </CalendarCell>
          </CalendarGridRow>
        </CalendarGridBody>
      </CalendarGrid>
    </div>
  </CalendarRoot>
</template>

<style scoped>
  /* CSS to hide the calendar popup and icon */
  .date-input::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }

  .date-input {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: textfield;
  }

  [data-selected='true'] {
    @apply bg-purple-500 !important;
    @apply hover:bg-purple-600 !important;
    @apply focus:bg-purple-600 !important;
    @apply focus:border !important;
    @apply focus:border-purple-500 !important;
  }
</style>
